import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { gateNexusSubscription } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { from, switchMap } from 'rxjs';
import { PudoLocation } from '../entities/PudoLocation.js';
import type { RouteOptimizationResult } from '../entities/RouteOptimizationResult.js';
import { geocodeAddress } from '../server/google-cloud/geocodeAddress.js';

export const subscribePlanningPudoLocations = registerSubscription({
  operationName: 'subscribePlanningPudoLocations',
  type: () => [PudoLocation],
  params: [{ name: 'routePlanningId', type: () => String }],
  resolve: (routePlanningId: string) => (_gqlFields: BaseQLSelectionSet<PudoLocation>) => {
    return gateNexusSubscription(NexusPermission.RoutePlanning, () =>
      watchQuery<RouteOptimizationResult>('route-plannings', { _id: routePlanningId, type: 'Dell' }, { limit: 1 }).pipe(
        switchMap(results =>
          from(
            (async () => {
              const planning = results[0];
              if (!planning) return [];

              const ticketCountByPudo = new globalThis.Map<string, number>();
              const tickets = [
                ...(planning.ticketsToSchedule ?? []),
                ...(planning.ticketsWithFETomorrow ?? []),
                ...(planning.unscheduledTickets ?? []),
              ];

              for (const ticket of tickets) {
                const pudoName = ticket.pudoName?.trim();
                if (!pudoName) continue;
                ticketCountByPudo.set(pudoName, (ticketCountByPudo.get(pudoName) ?? 0) + 1);
              }

              const pudoLocations = await Promise.all(
                Array.from(ticketCountByPudo.entries()).map(async ([name, ticketCount]) => {
                  try {
                    const coordinates = await geocodeAddress(name);
                    if (!coordinates) return null;
                    return new PudoLocation({
                      name,
                      lat: coordinates.latitude,
                      lng: coordinates.longitude,
                      ticketCount,
                    });
                  } catch {
                    return null;
                  }
                }),
              );

              return pudoLocations.filter((pudo): pudo is PudoLocation => pudo !== null);
            })(),
          ),
        ),
      ),
    );
  },
});
